import type { HomeTask, ThirdPartyEvent } from '@/types/diary';
import { getDatePart } from '@/utils/date';

export interface HomeworkItem {
  id: string;
  journalName: string;
  homeTasks: HomeTask[];
  events: ThirdPartyEvent[];
}

export interface HomeworkGroup {
  date: string;
  items: HomeworkItem[];
}

interface Params {
  homeTasks: HomeTask[];
  thirdPartyEvents: ThirdPartyEvent[];
}

export const buildHomeworkGroups = ({
  homeTasks,
  thirdPartyEvents,
}: Params): HomeworkGroup[] => {
  const itemsByDay = new Map<string, Map<string, HomeworkItem>>();

  const findOrCreateItem = (date: string, journalName: string) => {
    const day = getDatePart(date);
    const dayItems = itemsByDay.get(day) ?? new Map<string, HomeworkItem>();
    itemsByDay.set(day, dayItems);

    const item = dayItems.get(journalName) ?? {
      id: `${day}-${journalName}`,
      journalName,
      homeTasks: [],
      events: [],
    };
    dayItems.set(journalName, item);

    return item;
  };

  for (const homeTask of homeTasks) {
    findOrCreateItem(homeTask.lessonDate, homeTask.journalName).homeTasks.push(
      homeTask,
    );
  }

  for (const event of thirdPartyEvents) {
    findOrCreateItem(event.date, event.journalName).events.push(event);
  }

  return [...itemsByDay.entries()]
    .sort(([day], [nextDay]) => day.localeCompare(nextDay))
    .map(([date, dayItems]) => ({ date, items: [...dayItems.values()] }));
};
